import React, { useState } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency } from '../../utils/formatters';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { TrendingUp, ChevronLeft, BarChart2, Save, RefreshCw, DollarSign, Zap, Calendar, BarChart3, Target, Award, Shield, Plus, Minus, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import ResultsSummary from '../ResultsSummary';
import DynamicChartSystem from '../charts/DynamicChartSystem';
import ExcelTableV5 from '../ExcelTableV5';
import { getCertificatePhasesSummary } from '../../utils/calculations/certificateEvolution';
import { getDetailedCertificateEvolution } from '../../utils/calculations/certificateEvolution';
import DownloadReportButton from '../DownloadReportButton';
import PatrimonyChart from '../charts/PatrimonyChart';
import IncomeChart from '../charts/IncomeChart';
import CashFlowChart from '../charts/CashFlowChart';
import CompoundInterestMultiplier from '../compoundInterest/CompoundInterestMultiplier';
import { getLanguageContent, getLanguageFromCurrency } from '../../data/languages';

interface TraditionalResultsProps {
  onNext: () => void;
  onPrevious: () => void;
  onReset: () => void;
}

const TraditionalResults: React.FC<TraditionalResultsProps> = ({ onPrevious, onReset, onNext }) => {
  const { 
    investment, 
    results,
    patrimonyChartVisibility,
    incomeChartVisibility,
    cashFlowChartVisibility,
    togglePatrimonyChartElement,
    toggleIncomeChartElement,
    toggleCashFlowChartElement,
    resetPatrimonyChartVisibility,
    resetIncomeChartVisibility,
    resetCashFlowChartVisibility
  } = useCalculator();
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);
  
  const [showExcelTable, setShowExcelTable] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'compoundInterest' | 'patrimony' | 'income' | 'cashflow' | 'excel'>('summary');
  
  if (!results) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">
            {currentLanguage === 'es' ? 'Calculando resultados...' :
             currentLanguage === 'en' ? 'Calculating results...' :
             'Calcul des résultats...'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {currentLanguage === 'es' ? 'Procesando datos de inversión tradicional' :
             currentLanguage === 'en' ? 'Processing traditional investment data' :
             'Traitement des données d\'investissement traditionnel'}
          </p>
        </div>
      </div>
    );
  }

  // Get detailed certificate evolution and phases
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  const phases = getCertificatePhasesSummary(certificateEvolution, investment.years);

  if (showExcelTable) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <BarChart2 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {currentLanguage === 'es' ? 'Tabla de Evolución Detallada - Tradicional' :
                   currentLanguage === 'en' ? 'Detailed Evolution Table - Traditional' :
                   'Tableau d\'Évolution Détaillée - Traditionnel'}
                </h2>
                <p className="text-green-100">
                  {currentLanguage === 'es' ? 'Análisis detallado año por año de tu inversión tradicional' :
                   currentLanguage === 'en' ? 'Detailed year-by-year analysis of your traditional investment' :
                   'Analyse détaillée année par année de votre investissement traditionnel'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <ExcelTableV5 
              hideAdditionalOptions={true}
              initialCertificates={investment.initialCertificates} 
              years={investment.years}
            />
            
            <div className="mt-8 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowExcelTable(false)}
                className="px-8 py-3 bg-green-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>
                  {currentLanguage === 'es' ? 'Volver a Resultados' :
                   currentLanguage === 'en' ? 'Back to Results' :
                   'Retour aux Résultats'}
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="p-8 space-y-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'summary' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Resumen' :
               currentLanguage === 'en' ? 'Summary' :
               'Résumé'}
            </button>
            <button
              onClick={() => setActiveTab('compoundInterest')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'compoundInterest' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Interés Compuesto' :
               currentLanguage === 'en' ? 'Compound Interest' :
               'Intérêt Composé'}
            </button>
            <button
              onClick={() => setActiveTab('patrimony')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'patrimony' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Patrimonio' :
               currentLanguage === 'en' ? 'Patrimony' :
               'Patrimoine'}
            </button>
            <button
              onClick={() => setActiveTab('income')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'income' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Ingresos' :
               currentLanguage === 'en' ? 'Income' :
               'Revenus'}
            </button>
            <button
              onClick={() => setActiveTab('cashflow')}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'cashflow' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Flujo' :
               currentLanguage === 'en' ? 'Cash Flow' :
               'Flux de Trésorerie'}
            </button>
            <button
              onClick={() => setShowExcelTable(true)}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === 'excel' 
                  ? 'text-green-600 border-b-2 border-green-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {currentLanguage === 'es' ? 'Tabla Excel' :
               currentLanguage === 'en' ? 'Excel Table' :
               'Tableau Excel'}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'summary' && (
            <>
              {/* Resultados Finales Cosecha Capital */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl shadow-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">{content.traditional.results.title}</h3>
                    <p className="text-xs text-green-100">{content.traditional.results.subtitle.replace('{years}', investment.years.toString())}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-green-100 mb-1">{content.traditional.results.initialCertificates}</div>
                    <div className="text-2xl font-bold">
                      {investment.initialCertificates}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">{content.traditional.results.fromReinvestment}</div>
                    <div className="text-2xl font-bold">
                      +{results.certificatesSummary.fromReinvestment}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">{content.traditional.results.producingCertificates}</div>
                    <div className="text-2xl font-bold">
                      {phases.producing}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">{content.traditional.results.waiting}</div>
                    <div className="text-2xl font-bold">
                      {phases.waiting}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-green-100 mb-1">{content.traditional.results.monthlyIncome}</div>
                    <div className="text-xl font-bold">
                      {formatCurrency(results.finalMonthlyIncome, investment.currencyFormat)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-green-400">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-green-100 mb-1">{content.traditional.results.totalCertificates}</div>
                      <div className="text-4xl font-extrabold">
                        {results.certificatesSummary.totalCertificates}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-green-100 mb-1">{content.traditional.results.finalPatrimony}</div>
                      <div className="text-3xl font-bold">
                        {formatCurrency(results.finalPatrimony, investment.currencyFormat)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Métricas Detalladas */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <ResultsSummary />
              </div>
              
              {/* Excel Table Button */}
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowExcelTable(true)}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  <BarChart2 className="w-5 h-5" />
                  <span>
                    {currentLanguage === 'es' ? 'Ver Tabla de Evolución Detallada' :
                     currentLanguage === 'en' ? 'View Detailed Evolution Table' :
                     'Voir le Tableau d\'Évolution Détaillée'}
                  </span>
                </motion.button>
              </div>
              
              {/* Download Report Button */}
              <DownloadReportButton />
            </>
          )}

          {activeTab === 'compoundInterest' && (
            <div className="min-h-[500px]">
              <CompoundInterestMultiplier />
            </div>
          )}

          {activeTab === 'patrimony' && (
            <div className="min-h-[500px]">
              <PatrimonyChart />
            </div>
          )}

          {activeTab === 'income' && (
            <div className="min-h-[500px]">
              <IncomeChart />
            </div>
          )}

          {activeTab === 'cashflow' && (
            <div className="min-h-[500px]">
              <CashFlowChart />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{content.common.buttons.previous}</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onReset}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              <span>
                {currentLanguage === 'es' ? 'Comenzar de Nuevo' :
                 currentLanguage === 'en' ? 'Start Over' :
                 'Recommencer'}
              </span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TraditionalResults;